// src/pages/testimonials/views/Testimonials.view.tsx
import React, { useState } from "react";
import Header from "../../../components/header/Header";
import LazyLoadedSection from "../../../components/lazyloadedsection/LazyLoadedSection.view";
import { TestimonialsViewProps } from "../types";
import i18n from "../../../i18n/i18n";
import Button from "../../../components/global/Button";

import {
  TestimonialsContainer,
  PageTitle,
  CarouselWrapper,
  CarouselViewport,
  Slides,
  Slide,
  Card,
  Name,
  Message,
  NavButton,
  CTAContainer,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalClose,
  ModalForm,
  ModalLabel,
  ModalInput,
  ModalTextarea,
  ModalSelect,
  ModalActions,
  ModalError,
} from "../Testimonials.styled";

export function TestimonialsView({
  isLoading,
  testimonials,
  onSubmitTestimonial,
}: TestimonialsViewProps) {
  const [index, setIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [author, setAuthor] = useState("");
  const [message, setMessage] = useState("");
  const [language, setLanguage] = useState<"pt" | "en" | "es">("pt");
  const [errors, setErrors] = useState<{ author?: string; message?: string; language?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<"ok" | "error" | null>(null);
  const t = (key: string, def?: string) => (i18n as any).t(key, def);

  const prev = () => setIndex(i => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setIndex(i => (i + 1) % testimonials.length);

  const resetModalState = () => {
    setAuthor("");
    setMessage("");
    setErrors({});
    setFeedback(null);
  };

  const handleOpenModal = () => {
    resetModalState();
    setModalOpen(true);
  };

  const validate = () => {
    const e: { author?: string; message?: string; language?: string } = {};
    if (!author.trim() || author.trim().length < 2) e.author = "Nome muito curto (mín. 2 caracteres)";
    if (!message.trim() || message.trim().length < 10)
      e.message = "Depoimento muito curto (mín. 10 caracteres)";
    if (!language) e.language = "Idioma é obrigatório";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    const result = await onSubmitTestimonial({
      author: author.trim(),
      message: message.trim(),
      language,
    });
    setSubmitting(false);
    setFeedback(result);

    if (result === "ok") {
      setTimeout(() => {
        setModalOpen(false);
        resetModalState();
      }, 2000);
    }
  };

  return (
    <>
      <Header />
      <main>
        <TestimonialsContainer>
          <PageTitle>{t("testimonials:title", "Depoimentos")}</PageTitle>

          <LazyLoadedSection visible={!isLoading}>
            <CarouselWrapper>
              <NavButton aria-label="Anterior" onClick={prev}>
                ‹
              </NavButton>
              <CarouselViewport>
                <Slides index={index}>
                  {testimonials.map(item => (
                    <Slide key={item.id}>
                      <Card>
                        <Name>{item.name}</Name>
                        <Message>{item.message}</Message>
                      </Card>
                    </Slide>
                  ))}
                </Slides>
              </CarouselViewport>
              <NavButton aria-label="Próximo" onClick={next}>
                ›
              </NavButton>
            </CarouselWrapper>

            <CTAContainer>
              <Button onClick={handleOpenModal} ariaLabel={t("testimonials:leaveTestimonial", "Deixe seu depoimento")}>
                {t("testimonials:leaveTestimonial", "Deixe seu depoimento")}
              </Button>
            </CTAContainer>
          </LazyLoadedSection>
        </TestimonialsContainer>
      </main>

      {modalOpen && (
        <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="hubspot-modal-title">
          <ModalContent>
            <ModalHeader>
              <ModalTitle id="hubspot-modal-title">{t("testimonials:modal.title", "Deixe seu depoimento")}</ModalTitle>
              <ModalClose onClick={() => { setModalOpen(false); resetModalState(); }} aria-label={t("common:menuClose", "Fechar")}>
                ×
              </ModalClose>
            </ModalHeader>
            {feedback === "ok" && (
              <ModalError style={{ background: "#e6f7e6", color: "#1b6f24", padding: "0.75rem", borderRadius: 4 }}>
                {t("testimonials:modal.success", "Depoimento enviado! Obrigado.")}
              </ModalError>
            )}
            {feedback === "error" && (
              <ModalError>
                {t("testimonials:modal.error", "Erro ao enviar. Tente novamente em instantes.")}
              </ModalError>
            )}
            <ModalForm onSubmit={handleSubmit}>
              <div>
                <ModalLabel htmlFor="testimonial-language">{t("testimonials:modal.language", "Idioma")}</ModalLabel>
                <ModalSelect
                  id="testimonial-language"
                  value={language}
                  onChange={e => setLanguage(e.target.value as any)}
                  hasError={!!errors.language}
                >
                  <option value="pt">{t("common:languages.pt", "Português")}</option>
                  <option value="en">{t("common:languages.en", "English")}</option>
                  <option value="es">{t("common:languages.es", "Español")}</option>
                </ModalSelect>
                {errors.language && <ModalError>{errors.language}</ModalError>}
              </div>
              <div>
                <ModalLabel htmlFor="testimonial-name">{t("testimonials:modal.name", "Nome")}</ModalLabel>
                <ModalInput
                  id="testimonial-name"
                  type="text"
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                  hasError={!!errors.author}
                  placeholder={t("testimonials:modal.placeholderName", "Seu nome")}
                />
                {errors.author && <ModalError>{errors.author}</ModalError>}
              </div>
              <div>
                <ModalLabel htmlFor="testimonial-message">{t("testimonials:modal.message", "Depoimento")}</ModalLabel>
                <ModalTextarea
                  id="testimonial-message"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  hasError={!!errors.message}
                  placeholder={t("testimonials:modal.placeholderMessage", "Escreva seu depoimento...")}
                />
                {errors.message && <ModalError>{errors.message}</ModalError>}
              </div>
              <ModalActions>
                <Button variant="secondary" onClick={() => { setModalOpen(false); resetModalState(); }} ariaLabel={t("common:cancel", "Cancelar")}>
                  {t("common:cancel", "Cancelar")}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || feedback === "ok"}
                  ariaLabel={t("common:send", "Enviar depoimento")}
                >
                  {submitting
                    ? t("common:sending", "Enviando...")
                    : t("common:send", "Enviar")}
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default TestimonialsView;
